import '@/settings/color.css';
import '@/assets/css/index.scss';

import { autoAnimatePlugin } from '@formkit/auto-animate/vue';
import { createHead } from '@unhead/vue/client';
import { vMaska } from 'maska/vue';
import { createPinia } from 'pinia';
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate';
import { createApp } from 'vue';

import App from '@/App.vue';
import EmptyList from '@/components/base/EmptyList.component.vue';
import IsLoading from '@/components/base/loadings/Index.component.vue';
import Pagination from '@/components/shared/Pagination.component.vue';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label/index';
import {
  Select as SelectBase,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import Skeleton from '@/components/ui/skeleton/Skeleton.vue';
import SkeletonTitle from '@/components/ui/skeleton/SkeletonTitle.vue';
import { useGlobalNotificationStore } from '@/modules/notification/store/GlobalNotification.store';
import { usePipelineStore } from '@/modules/pipeline/stores/Pipeline.store';
import { useUserStore } from '@/modules/user/stores/users.store';
import { FontAwesomeIcon, setupFontAwesome } from '@/plugins/fontawesome';
import { ensureSwiperElementsRegistered } from '@/plugins/swiper';
import { scheduleThirdPartyScripts } from '@/plugins/thirdParty';
import router from '@/router/index';
import i18n from '@/settings/i18n/index';
import image from '@/settings/images';
import systemConstants from '@/settings/systemConstants';
import { useFeatureFlagsStore } from '@/stores/featureFlag.store';
import { useSubsidiaryStore } from '@/stores/subsidiary.store';

const app = createApp(App);
const pinia = createPinia();
pinia.use(piniaPluginPersistedstate);
setupFontAwesome();
void ensureSwiperElementsRegistered();
void scheduleThirdPartyScripts();

app.use(i18n);
app.use(pinia);
app.use(autoAnimatePlugin);
app.use(router);
app.use(createHead());

app.directive('maska', vMaska);

app.component('Card', Card);
app.component('InputBase', Input);
app.component('LabelBase', Label);
app.component('ButtonBase', Button);
app.component('FormItem', FormItem);
app.component('SkeletonBase', Skeleton);
app.component('CardTitle', CardTitle);
app.component('EmptyList', EmptyList);
app.component('FormField', FormField);
app.component('FormLabel', FormLabel);
app.component('IsLoading', IsLoading);
app.component('CardFooter', CardFooter);
app.component('CardHeader', CardHeader);
app.component('Pagination', Pagination);
app.component('SelectBase', SelectBase);
app.component('SelectItem', SelectItem);
app.component('CardContent', CardContent);
app.component('FormControl', FormControl);
app.component('FormMessage', FormMessage);
app.component('SelectGroup', SelectGroup);
app.component('SelectValue', SelectValue);
app.component('SelectContent', SelectContent);
app.component('SelectTrigger', SelectTrigger);
app.component('SkeletonTitle', SkeletonTitle);
app.component('CardDescription', CardDescription);
app.component('FontAwesomeIcon', FontAwesomeIcon);

app.config.globalProperties.$systemConstants = systemConstants;
app.config.globalProperties.$userStore = useUserStore();
app.config.globalProperties.$pipelineStore = usePipelineStore();
app.config.globalProperties.$featureStore = useFeatureFlagsStore();
app.config.globalProperties.$subsidiaryStore = useSubsidiaryStore();
app.config.globalProperties.$notificationStore = useGlobalNotificationStore();

app.mixin({
  created() {
    this.$options.imageConfig = image;
    this.$options.systemConstants = systemConstants;
    this.$options.userStore = app.config.globalProperties.$userStore;
  },
});

app.mount('#app');
